import { Form, FormProps, message } from 'antd';
import { SelectProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useSearchParams } from 'react-router';

import { FAIL_REPORT_URLS } from '@/constants/urls';
import { useUserContext } from '@/contexts/UserContext';
import useFormDraftInit from '@/hooks/useFormDraftInit';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';
import { ensureHtml } from '@/utils/ensureHtml';
// Mejora: se reemplazó el template literal duplicado por formatTicketLabel centralizado
import { formatTicketLabel } from '@/utils/formHelpers';

import { TicketFailItem } from './useTicketFormFailInfo';

const normalizeDescription = (description?: string | null) => {
  if (!description) {
    return '';
  }

  const hasWrapper = description.includes('<div id="description">');
  const sanitized = hasWrapper
    ? description.replace(/^<div id="description">|<\/div>$/g, '')
    : description;

  return ensureHtml(sanitized);
};

const flattenInitialValues = (values?: Record<string, any>) => {
  if (!values) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value && typeof value === 'object' && 'id' in value ? value.id : value,
    ]),
  );
};

export const useTicketForm = (
  formProps: FormProps,
  edit?: boolean,
  setBlockTabs?: (block: boolean) => void,
) => {
  // Seteo de valores iniciales y reseteo de campos
  const { initialValues } = formProps;
  const [sp] = useSearchParams();
  const machineId = sp.get('movil_id') ? Number(sp.get('movil_id')) : undefined;
  const empresaId = sp.get('empresa_id') ? Number(sp.get('empresa_id')) : undefined;
  // Reporte de problema automático ya existente en el ticket (si lo hay), para precargar el form
  const failReportInitial = initialValues?.fail_report;
  const partialInitialValues = useMemo(() => {
    if (edit) {
      if (!initialValues) {
        return undefined;
      }

      const normalizedDescription = normalizeDescription(initialValues?.description);
      // `fail_report` se descarta del spread: es un sub-recurso embebido, no una relación simple,
      // y `flattenInitialValues` lo aplanaría a su sólo `id` (perdiendo categoría/garantía/comentario/items).
      const { fail_report, ...restInitialValues } = initialValues;

      return flattenInitialValues({
        ...restInitialValues,
        description: normalizedDescription,
        fail_report_category: fail_report?.category?.id ?? fail_report?.category,
        fail_report_warranty: fail_report?.warranty,
        fail_report_comment: fail_report?.comment,
        // Mismo shape que useTicketFormFailInfo termina sincronizando al campo oculto,
        // para que useFormDraftInit no detecte un cambio falso apenas carga el form.
        fail_report_items: (fail_report?.fail_report_items ?? []).map((item: any) => ({
          product: item.product ?? null,
          device: item.device ?? null,
          unidad: item.unidad ?? null,
          comment: item.comment || undefined,
        })),
      });
    }

    return (
      flattenInitialValues({
        ...initialValues,
        status: initialValues?.status ?? 1,
        movil: initialValues?.movil ? initialValues.movil : (machineId ?? undefined),
        empresa: initialValues?.empresa ? initialValues.empresa : (empresaId ?? undefined),
        title: initialValues?.title ? initialValues.title : initialValues?.ticket_title_str,
      }) ?? { status: 1 }
    );
  }, [edit, empresaId, initialValues, machineId]);
  // Form para inicialización de draft
  const { initialValuesSetted, formInitialValues, clearDraft, hasDraftChanges } = useFormDraftInit({
    form: formProps.form,
    draftEnabled: !edit || Boolean((initialValues as { id?: string | number })?.id),
    initialValues: partialInitialValues,
    excludedDraftFields: ['ia_timeline', 'logistics_props'],
  });

  // Bloqueo de pestaña logística si hay cambios en el draft
  useEffect(() => {
    if (hasDraftChanges) {
      setBlockTabs?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDraftChanges]);
  /**
   * Selectores
   */
  const companyId = Form.useWatch('empresa', formProps.form);
  const { companySelectProps, subCategorySelectProps, usersSelectProps, subStatusSelectProps } =
    useOutletContext<{
      usersSelectProps: SelectProps;
      companySelectProps: SelectProps;
      subStatusSelectProps: SelectProps;
      subCategorySelectProps: SelectProps;
    }>();
  const { selectProps: machineSelectProps } = useSelectScrollSearch(
    {
      resource: 'moviles',
      optionLabel: (item: any) => `${item.description} (${item.id})`,
      optionValue: (item: any) => item.id,
      filters: [
        {
          value: companyId,
          operator: 'eq' as const,
          field: 'empresa',
        },
      ],

      meta: {
        paginate: 1,
        full: 0,
        showNoUnit: true,
        showDisabled: true,
      },
    },
    { saveItem: true },
  );
  const { selectProps: refTicketsSelectProps } = useSelectScrollSearch({
    resource: RESOURCES.TICKETS,
    optionLabel: formatTicketLabel,
    optionValue: (item: any) => item.id,
  });
  /**
   * Para el caso de edición, se buscan y se setean los valores iniciales
   * Se debe retornar un objeto con los selectProps modificados para incluir los valores iniciales
   * y que el cambio sea reconocido por el selector
   */
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps, // viene del contexto/superior
    resource: 'empresas',
    initialValue: formInitialValues?.empresa,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const machineSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: machineSelectProps,
    resource: 'moviles',
    initialValue: formInitialValues?.movil,
    labelKey: item => `${item.description} (${item.id})`,
    valueKey: 'id',
  });
  const refTicketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: refTicketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: formInitialValues?.referencia,
    labelKey: formatTicketLabel,
    valueKey: 'id',
  });
  /**
   *  Autocompletado de empresa al seleccionar maquina y reseteo
   */
  const companySelected = Form.useWatch('empresa', formProps.form);
  const [machineSelectedItem, setMachineSelectedItem] = useState<any>();
  const machineCompanyOption = useMemo(
    () =>
      machineSelectedItem?.empresa
        ? [
            {
              label: machineSelectedItem.empresa.full_name ?? machineSelectedItem.empresa_str,
              value: machineSelectedItem.empresa.id ?? machineSelectedItem.empresa,
            },
          ]
        : [],
    [machineSelectedItem],
  );
  const machineSelectPropsModified = {
    ...machineSelectPropsWithInitials,
    onChange: (v, opt: any) => {
      machineSelectProps?.onChange?.(v, opt);
      setMachineSelectedItem(opt ? opt.item : undefined);
    },
  };
  const companySelectPropsModified = useMemo(
    () => ({
      ...companySelectPropsWithInitials,
      options: [
        ...machineCompanyOption,
        ...(companySelectPropsWithInitials.options?.filter(
          c => c.value != machineCompanyOption[0]?.value,
        ) || []),
      ],
      onChange: (v, opt: any) => {
        companySelectPropsWithInitials?.onChange?.(v, opt);
        formProps.form?.setFieldValue('movil', null);
        setMachineSelectedItem(undefined);
      },
    }),
    [companySelectPropsWithInitials, formProps.form, machineCompanyOption],
  );

  useEffect(() => {
    if (machineSelectedItem && machineCompanyOption.length > 0 && !companySelected) {
      formProps.form?.setFieldValue('empresa', machineCompanyOption[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineCompanyOption]);

  /**
   * setear usuario asignado por defecto al usuario logueado
   */

  // FIXME deberia setearse por username
  const { userLogged } = useUserContext();

  useEffect(() => {
    if (!initialValues?.assigned_to && userLogged && initialValuesSetted) {
      const userLoggedValue = usersSelectProps?.options?.find(
        opt => opt.item?.username === userLogged?.username,
      )?.value;

      formProps.form?.setFieldValue('assigned_to', userLoggedValue);
    }
  }, [
    formProps.form,
    initialValues?.assigned_to,
    initialValuesSetted,
    userLogged,
    usersSelectProps?.options,
  ]);

  /**
   * Lógica para tomar un ticket como propio en caso de acceder desde el mail con parametro "take_ticket=1"
   */
  const { t } = useTranslation();
  const { ticketRepository } = useRepositoriesContext();
  const takeTicket = sp.get('take_ticket') ? Number(sp.get('take_ticket')) : undefined;
  const [takingTicket, setTakingTicket] = useState<boolean>(false);

  useEffect(() => {
    if (!formInitialValues || !takeTicket || takingTicket || formInitialValues.assigned_to) {
      return;
    }

    const takeTicketIfNeeded = async () => {
      setTakingTicket(true);
      ticketRepository
        .updateTicket({ id: formInitialValues?.id, assigned_to_username: userLogged?.username })
        .then(() => {
          message.success(t('El ticket fue tomado correctamente'));
          const userLoggedValue = usersSelectProps?.options?.find(
            opt => opt.item?.username === userLogged?.username,
          )?.value;

          formProps.form?.setFieldValue('assigned_to', userLoggedValue);
        })
        .catch(() => {
          message.error(t('Ocurrió un error al tomar el ticket'));
        })
        .finally(() => {
          setTakingTicket(false);
        });
    };

    void takeTicketIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeTicket, ticketRepository]);

  /**
   * Chequeo para reporte de problemas
   */
  const categorySelected = Form.useWatch('subcategoria', formProps.form);
  // Si el ticket ya tiene un reporte automático, se muestra/edita aunque la subcategoría
  // actual ya no exija uno (para no esconder datos existentes).
  const hasExistingFailReport = Boolean(failReportInitial);
  const needFailReportData = useMemo(() => {
    const category = subCategorySelectProps.options?.find(
      opt => opt.value === categorySelected,
    )?.item;

    return Boolean(category?.fail_report_required) || hasExistingFailReport;
  }, [categorySelected, subCategorySelectProps.options, hasExistingFailReport]);
  const { selectProps: failCategorySelectProps } = useSelectScrollSearch({
    resource: FAIL_REPORT_URLS.FAIL_CATEGORIES,
    optionLabel: item => item.title,
    optionValue: item => item.id,
    queryOptions: { enabled: needFailReportData },
  });
  // La categoría del reporte ya viene con su título embebido en el ticket; se agrega como
  // opción inicial para no depender de que el scroll infinito la haya paginado todavía.
  const failReportCategoryInitialOption = useMemo(
    () =>
      failReportInitial?.category
        ? [{ label: failReportInitial.category.title, value: failReportInitial.category.id }]
        : [],
    [failReportInitial],
  );
  const failCategorySelectPropsWithInitial = useMemo(
    () => ({
      ...failCategorySelectProps,
      options: [
        ...failReportCategoryInitialOption,
        ...(failCategorySelectProps.options?.filter(
          opt => opt.value !== failReportCategoryInitialOption[0]?.value,
        ) ?? []),
      ],
    }),
    [failCategorySelectProps, failReportCategoryInitialOption],
  );
  // Ítems ya cargados en el reporte automático del ticket, mapeados al shape local de la tabla.
  // `product` siempre viene resuelto; device/unidad son adicionales y no deben pisarlo.
  const failReportInitialItems = useMemo<TicketFailItem[]>(
    () =>
      (failReportInitial?.fail_report_items ?? []).map((item: any) => ({
        key: item.id != null ? String(item.id) : `${item.product}-${item.name}`,
        product_type: item.product_type,
        product: item.product,
        device: item.device ?? undefined,
        unidad: item.unidad ?? undefined,
        name: item.name,
        serial_number: item.serial_number,
        comment: item.comment,
      })),
    [failReportInitial],
  );

  return {
    subCategorySelectProps,
    usersSelectProps,
    subStatusSelectProps,
    companySelectProps: companySelectPropsModified,
    machineSelectProps: machineSelectPropsModified,
    refTicketsSelectProps: refTicketsSelectPropsWithInitials,
    formInitialValues,
    clearDraft,
    needFailReportData,
    failCategorySelectProps: failCategorySelectPropsWithInitial,
    failReportInitialItems,
  };
};
